import React, { FC, memo } from 'react';
import dynamic from 'next/dynamic';
import Box from '@material-ui/core/Box';
import Grid, { GridSpacing } from '@material-ui/core/Grid';
import styled from 'styled-components';

import { DownloadFileData } from '@keaze/web/components/downloadFile';
import {
  ListingDetails,
  ListingDetailsHousingProvider,
  PropertyDetails,
  VideoDetails,
} from '@keaze/web/common/interfaces';
import { Overview } from '../overview';
import { StartRegistration } from '../startRegistration';
import { OnVideoClick } from '@keaze/web/components/videos';
import { PropertyListingProps } from '../propertyListing';
import {
  FiltersContainer,
  ItemStartRegistration,
  MobileStartRegistration,
  StyledGroupDownloadFile,
} from './property.styles';
import { useProperty } from './property.hook';
import { PropertyProps, OnPlanClick } from './property.types';
import { AvailabilityStatusCode } from '@keaze/web/common/enums';
import { PlanModalProps } from '../planModal';
import { FAQ_DATA } from '@keaze/web/utils/data/faq';
import { OnRegisterButtonClick } from '../listingDetails.types';
import { VideosSection } from '../videosSection';
import { LeftSide } from '../listingDetails.styles';
import { FAQScheme } from 'microdataSchemas';
import { GroupAccordionProps } from '@keaze/web/components/groupAccordion';
import { convertPropertiesToPlans } from '../helpers/converts';
import { BedroomFilters } from './bedroomFilters';

const S = {
  Disclaimer: styled.p`
    font-size: 12px;
    color: #003c3b;
  `,

  Grid: styled(Grid)`
    .MuiAccordionSummary-content span {
      color: #003c3b !important;
    }
  `,

  mobileReg: styled(MobileStartRegistration)`
    ul {
      display: none;
    }
    ${({ theme: { breakpoints } }) => `
    display: block;
    border: 1px solid #672500;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    top: 1rem;
    margin: auto;
    width: auto;
   height: auto;
    box-shadow: 0px -2px 4px 0px rgba(0, 0, 0, 0.25);
    border-radius: 0;
    button {
      background-color: #672500;
      width: 214px;
      height: 41px;
      bottom: 15px;
    }
    ${breakpoints.up('xs')} { 
      width: auto;
      
          }
          ${breakpoints.up('sm')} { 
            width: 90%;
            
                }
        ${breakpoints.up('md')} {
          display: none;
        }

`}
  `,
};

const DynamicPropertyListing = dynamic<PropertyListingProps>(() =>
  import('../propertyListing').then((model) => model.PropertyListing)
);

const DynamicGroupAccordion = dynamic<GroupAccordionProps>(() =>
  import('@keaze/web/components/groupAccordion').then(
    (model) => model.GroupAccordion
  )
);

const DynamicPlanModal = dynamic<PlanModalProps>(
  () => import('../planModal').then((model) => model.PlanModal),
  { ssr: false }
);

type RenderFiltersSectionArgs = {
  listing: ListingDetails;
  spacing: GridSpacing;
  address: string;
  videos: VideoDetails[] | null;
  isVisibleFilters: boolean;
  isListingLoading: boolean;
  bedrooms: number[];
  propertiesForListing: PropertyDetails[];
  isRegisterInterestLoading: boolean;
  onRegisterButtonClick: OnRegisterButtonClick;
  onVideoClick: OnVideoClick;
};

type RenderFiltersSection = (args: RenderFiltersSectionArgs) => JSX.Element;

type RenderPropertyListingArgs = {
  tooltipTextForPrice: string[];
  housingProvider: ListingDetailsHousingProvider;
  propertiesForListing: PropertyDetails[];
  onRegisterButtonClick: OnRegisterButtonClick;
  onPlanClick: OnPlanClick;
};

type RenderPropertyListing = (args: RenderPropertyListingArgs) => JSX.Element;

type RenderInfoArgs = {
  sitePlanImagesDownloadFiles: DownloadFileData[];
  brochuresDownloadFiles: DownloadFileData[];
  longDescription: string | null;
  spacing: GridSpacing;
};

type RenderInfo = (args: RenderInfoArgs) => JSX.Element;

type GenerateTooltipTextForPriceArgs = {
  minShareValue: number | null;
  depositPercentage: number | null;
  depositValue: number | null;
};

type GenerateTooltipTextForPrice = (
  args: GenerateTooltipTextForPriceArgs
) => string[];

const renderFiltersSection: RenderFiltersSection = ({
  listing,
  spacing,
  address,
  videos,
  isVisibleFilters,
  isListingLoading,
  bedrooms,
  propertiesForListing,
  isRegisterInterestLoading,
  onVideoClick,
  onRegisterButtonClick,
}) => {
  const isEmptyVideos = videos !== null && videos.length === 0;

  if (isEmptyVideos && !isVisibleFilters) {
    return (
      <FiltersContainer container spacing={spacing} justifyContent="flex-end">
        <ItemStartRegistration item xs={12} md={5}>
          {/* <StartRegistration
            listing={listing}
            isLoading={isRegisterInterestLoading}
            isListingLoading={isListingLoading}
            onRegisterButtonClick={onRegisterButtonClick}
          /> */}
        </ItemStartRegistration>
      </FiltersContainer>
    );
  }

  return (
    <FiltersContainer container spacing={spacing}>
      <Grid item xs={12} md={7}>
        <VideosSection
          listing={listing}
          description={address}
          videos={videos}
          onVideoClick={onVideoClick}
        />
        {isVisibleFilters && (
          <BedroomFilters
            listing={listing}
            bedrooms={bedrooms}
            properties={propertiesForListing}
          />
        )}
      </Grid>
      {/* This ha now been moved to listing detial in line with designs */}
      {/* <ItemStartRegistration item xs={12} md={5}>
        <StartRegistration
          listing={listing}
          isLoading={isRegisterInterestLoading}
          isListingLoading={isListingLoading}
          onRegisterButtonClick={onRegisterButtonClick}
        />
      </ItemStartRegistration> */}
    </FiltersContainer>
  );
};

const renderPropertyListing: RenderPropertyListing = ({
  tooltipTextForPrice,
  housingProvider,
  propertiesForListing,
  onRegisterButtonClick,
  onPlanClick,
}) => (
  <Box mt={3} mb={3}>
    <DynamicPropertyListing
      data={propertiesForListing}
      tooltipTextForPrice={tooltipTextForPrice}
      housingProvider={housingProvider}
      onRegisterButtonClick={onRegisterButtonClick}
      onPlanClick={onPlanClick}
    />
  </Box>
);

const renderInfo: RenderInfo = ({
  sitePlanImagesDownloadFiles,
  brochuresDownloadFiles,
  longDescription,
  spacing,
}) => {
  const isBrochuresDownloadFiles = brochuresDownloadFiles.length !== 0;
  const issitePlanImagesDownloadFiles =
    sitePlanImagesDownloadFiles.length !== 0;

  return (
    <Box>
      <Grid container spacing={spacing}>
        <Grid item xs={12} md={7}>
          <LeftSide>
            <Overview description={longDescription ?? ''} />
          </LeftSide>
        </Grid>
        <S.Grid item xs={12} md={5}>
          {isBrochuresDownloadFiles && (
            <StyledGroupDownloadFile
              title="Brochures"
              data={brochuresDownloadFiles}
            />
          )}

          {sitePlanImagesDownloadFiles && (
            <StyledGroupDownloadFile
              title="Site Plan"
              data={sitePlanImagesDownloadFiles}
            />
          )}
          <DynamicGroupAccordion
            title="FAQs"
            data={FAQ_DATA}
            microdataScheme={FAQScheme}
          />
          <S.Disclaimer>Disclaimer</S.Disclaimer>
          <S.Disclaimer>
            The figures and estimates shown in this plot illustration should not
            be relied on as confirmation of affordability and should only be
            used as an indicative guide to monthly costs. The monthly rental
            cost is based on 2.75% on the retained equity (subject to change
            please ask your sales agent). This will usually increase by the
            annual rate of RPI or CPI. The monthly mortgage cost is based on a
            Variable Rate Mortgage at 5.50% over a 25-year repayment loan at a
            95% LTV. Other costs will apply such as service charge. Sage Homes
            is not authorised or regulated by the Financial Conduct Authority to
            provide financial advice. You should always seek advice and
            recommendations from an independent financial advisor regarding
            mortgage products, affordability, interest rates, repayment methods
            and terms and conditions. You should also seek independent legal
            advice. Your home may be repossessed if you do not keep up payments
            on a mortgage or any other loan secured against it.
          </S.Disclaimer>
        </S.Grid>
      </Grid>
    </Box>
  );
};

const generateTooltipTextForPrice: GenerateTooltipTextForPrice = ({
  minShareValue,
  depositPercentage,
  depositValue,
}) => {
  if (minShareValue && depositPercentage && depositValue) {
    return [
      'The price for the portion of property you buy.',
      `When working out your Loan to Value (LTV), it's done against this value, not the full price. For example, to buy a share costing £${minShareValue} with a ${depositPercentage}% deposit, you'll need a deposit of £${depositValue}.`,
    ];
  }

  return [
    'The price for the portion of property you buy.',
    "When working out your Loan to Value (LTV), it's done against this value, not the full price. For example, to get a 5% deposit, you'll need 5% of the share price.",
  ];
};

export const Property: FC<PropertyProps> = memo(
  ({
    listing,
    properties,
    spacing,
    address,
    isRegisterInterestLoading,
    isListingLoading,
    onVideoClick,
    onRegisterButtonClick,
  }) => {
    const {
      housingProvider,
      bedrooms,
      videos,
      name,
      availabilityStatus,
      longDescription,
      brochures,
      sitePlanImages,
      minShareValue,
      depositPercentage,
      depositValue,
    } = listing;
    const isListingSold =
      availabilityStatus.code === AvailabilityStatusCode.Sold;
    const {
      brochuresDownloadFiles,
      sitePlanImagesDownloadFiles,
      isVisibleFilters,
      propertiesForListing,
      onPlanClick,
    } = useProperty({
      properties,
      isListingSold,
      brochures,
      sitePlanImages,
    });
    const tooltipTextForPrice = generateTooltipTextForPrice({
      minShareValue,
      depositPercentage,
      depositValue,
    });

    return (
      <>
        {renderFiltersSection({
          listing,
          spacing,
          address,
          videos,
          isVisibleFilters,
          isListingLoading,
          bedrooms,
          propertiesForListing,
          isRegisterInterestLoading,
          onRegisterButtonClick,
          onVideoClick,
        })}
        {isVisibleFilters &&
          renderPropertyListing({
            tooltipTextForPrice,
            housingProvider,
            propertiesForListing,
            onRegisterButtonClick,
            onPlanClick,
          })}
        {renderInfo({
          brochuresDownloadFiles,
          longDescription,
          spacing,
          sitePlanImagesDownloadFiles,
        })}

        {}
        <S.mobileReg
          listing={listing}
          isLoading={isRegisterInterestLoading}
          isListingLoading={isListingLoading}
          onRegisterButtonClick={onRegisterButtonClick}
        />
        <DynamicPlanModal
          title={name}
          listing={listing}
          description={address}
          data={convertPropertiesToPlans(propertiesForListing)}
        />
      </>
    );
  }
);
